import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { NodeSelection } from 'prosemirror-state';
import { useContext, useState } from 'react';
import { useToast } from '../../../hooks/useToast/useToast';
import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { ImageEditButton } from './_components/ImageEditButton';
import { LinkButton } from './_components/LinkButton';
import { PreviewButton } from './_components/PreviewButton';
import { YouTubeButton } from './_components/YouTubeButton';
import { TEXT_ALIGN } from './_constants';
import type { CustomImageOptions } from './_utils/CustomImage';
import type { CustomLinkOptions } from './_utils/CustomLink';
import { extractAlignValue } from './_utils/extractAlignValue';
import { extractMaxWidthValue } from './_utils/extractMaxWidthValue';
import type { BizFontSizeType } from './_utils/FontSize';
import { BIZ_FONT_SIZES, DEFAULT_FONT_SIZE } from './_utils/FontSize';
import { FONT_WEIGHTS } from './_utils/FontWeight';
import { getYouTubeVideoId } from './_utils/getYouTubeVideoId';
import { HIGHLIGHT_LABELS, HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLORS } from './_utils/TextColor';
export interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
}
const DEFAULT_CUSTOM_IMAGE_OPTIONS = {
  maxWidth: null,
  isBorderRadius: false,
  align: 'left'
} as const;
const DEFAULT_CUSTOM_LINK_OPTIONS = {
  theme: 'text',
  size: 'H56',
  isOutLink: false,
  href: ''
} as const;
export const BizToolbar = t0 => {
  const $ = _c(108);
  const {
    imageUploadOptions
  } = t0;
  const {
    toast,
    toastError
  } = useToast();
  const {
    editor
  } = useContext(CustomEditorContext);
  const isImageSelected = editor?.state.selection instanceof NodeSelection && editor?.state.selection.node.type.name === "image";
  const [customImageOptions, setCustomImageOptions] = useState(DEFAULT_CUSTOM_IMAGE_OPTIONS);
  const [customLinkOptions, setCustomLinkOptions] = useState(DEFAULT_CUSTOM_LINK_OPTIONS);
  const [youTubeUrl, setYouTubeUrl] = useState("");
  let t1;
  if ($[0] !== editor) {
    const getSelectedNodeAttrText = attrName => {
      if (!editor) {
        return "";
      }
      const selection = editor.state.selection;
      if (!(selection instanceof NodeSelection)) {
        return "";
      }
      const attrs = selection.node.attrs as Record<string, unknown>;
      const value = attrs[attrName];
      return typeof value === "string" ? value : "";
    };
    t1 = () => {
      const styleText = getSelectedNodeAttrText("style");
      const classText = getSelectedNodeAttrText("class");
      const maxWidth = extractMaxWidthValue(styleText);
      const isBorderRadius = classText.includes("rounded");
      const align = extractAlignValue(classText);
      setCustomImageOptions({
        maxWidth,
        isBorderRadius,
        align
      });
    };
    $[0] = editor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialImageOptions = t1;
  let t2;
  if ($[2] !== isImageSelected) {
    t2 = () => {
      if (isImageSelected) {
        setCustomLinkOptions(_temp);
      }
    };
    $[2] = isImageSelected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const initialLinkOptions = t2;
  if (!editor) {
    return null;
  }
  let t3;
  if ($[4] !== editor) {
    t3 = TEXT_COLORS.find(t4 => {
      const [, color] = t4;
      return editor.isActive("textStyle", {
        color
      });
    }) ?? [];
    $[4] = editor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [selectedColor] = t3;
  let t4;
  if ($[6] !== editor) {
    t4 = FONT_WEIGHTS.find(fontWeight => editor.isActive("fontWeight", {
      fontWeight
    }));
    $[6] = editor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selectedFontWeight = t4;
  let t5;
  if ($[8] !== editor) {
    t5 = BIZ_FONT_SIZES.find(fontSize => editor.isActive("fontSize", {
      fontSize
    })) ?? DEFAULT_FONT_SIZE;
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selectedFontSize = t5;
  let isBoxedText;
  let t10;
  let t11;
  let t12;
  let t13;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== editor || $[11] !== selectedColor || $[12] !== selectedFontSize || $[13] !== selectedFontWeight) {
    isBoxedText = editor.isActive("boxedText");
    let t14;
    if ($[23] !== editor) {
      t14 = TEXT_ALIGN.find(textAlign => editor.isActive("textAlign", {
        textAlign
      }));
      $[23] = editor;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    const selectedTextAlign = t14;
    t8 = "flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8";
    let t15;
    if ($[25] !== editor) {
      t15 = e => {
        editor.chain().focus().setBizFontSize(e.target.value as BizFontSizeType).run();
      };
      $[25] = editor;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    let t16;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = BIZ_FONT_SIZES.map(_temp2);
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    if ($[28] !== selectedFontSize || $[29] !== t15) {
      t9 = <select aria-label={"\uAE00\uC790 \uD06C\uAE30"} value={selectedFontSize} onChange={t15}>{t16}</select>;
      $[28] = selectedFontSize;
      $[29] = t15;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    let t17;
    if ($[31] !== editor) {
      t17 = e_0 => {
        editor?.chain().focus().setTextAlign(e_0.target.value).run();
      };
      $[31] = editor;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = TEXT_ALIGN.map(_temp3);
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    if ($[34] !== selectedTextAlign || $[35] !== t17) {
      t10 = <select aria-label={"\uC815\uB82C"} value={selectedTextAlign} onChange={t17}>{t18}</select>;
      $[34] = selectedTextAlign;
      $[35] = t17;
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    let t19;
    if ($[37] !== editor || $[38] !== selectedColor) {
      t19 = TEXT_COLORS.map(t20 => {
        const [label, color_0] = t20;
        return <button key={label} className={cn("font-800", {
          underline: selectedColor === label
        })} style={{
          color: color_0
        }} type="button" onClick={() => editor?.chain().focus().setColor(color_0).run()}>A</button>;
      });
      $[37] = editor;
      $[38] = selectedColor;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    if ($[40] !== t19) {
      t11 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t19}</section>;
      $[40] = t19;
      $[41] = t11;
    } else {
      t11 = $[41];
    }
    let t20;
    if ($[42] !== editor || $[43] !== selectedFontWeight) {
      t20 = FONT_WEIGHTS.map(fontWeight_0 => <button key={fontWeight_0} className={cn(fontWeight_0, {
        underline: selectedFontWeight === fontWeight_0
      })} type="button" onClick={() => editor?.chain().focus().setFontWeight(fontWeight_0).run()}>B</button>);
      $[42] = editor;
      $[43] = selectedFontWeight;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    if ($[45] !== t20) {
      t12 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t20}</section>;
      $[45] = t20;
      $[46] = t12;
    } else {
      t12 = $[46];
    }
    if ($[47] !== editor) {
      t13 = HIGHLIGHTS.map(highlight => {
        const isActive = editor.isActive("highlight", {
          highlight
        });
        return <button key={highlight} aria-label={HIGHLIGHT_LABELS[highlight]} className={cn(highlight, "flex size-24 min-h-24 min-w-24 items-center justify-center rounded-xsmall font-700", {
          "border-1 border-Gray-800": isActive
        })} type="button" onClick={() => isActive ? editor.chain().focus().unsetHighlight().run() : editor.chain().focus().setHighlight(highlight).run()} />;
      });
      $[47] = editor;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    t6 = "\uBC15\uC2A4\uD14D\uC2A4\uD2B8";
    t7 = cn("box-border flex size-24 items-center justify-center", {
      "border-Gray-800 border-1 rounded-xxsmall": isBoxedText
    });
    $[10] = editor;
    $[11] = selectedColor;
    $[12] = selectedFontSize;
    $[13] = selectedFontWeight;
    $[14] = isBoxedText;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    isBoxedText = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t14;
  if ($[49] !== editor || $[50] !== isBoxedText) {
    t14 = () => isBoxedText ? editor.chain().focus().unsetBoxedText().run() : editor.chain().focus().setBoxedText().run();
    $[49] = editor;
    $[50] = isBoxedText;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <BoxedTextIcon />;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t14 || $[54] !== t6 || $[55] !== t7) {
    t16 = <button aria-label={t6} className={t7} type="button" onClick={t14}>{t15}</button>;
    $[53] = t14;
    $[54] = t6;
    $[55] = t7;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== editor) {
    t17 = () => {
      editor?.chain().focus().setHorizontalRule().run();
    };
    $[57] = editor;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <HrIcon />;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== t17) {
    t19 = <button aria-label={"\uAD6C\uBD84\uC120 \uC0BD\uC785"} type="button" onClick={t17}>{t18}</button>;
    $[60] = t17;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== imageUploadOptions) {
    t20 = <ImageButton {...imageUploadOptions} />;
    $[62] = imageUploadOptions;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  const t21 = !isImageSelected;
  let t22;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = align_0 => {
      setCustomImageOptions(prev_0 => ({
        ...prev_0,
        align: align_0
      }));
    };
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = isBorderRadius_0 => {
      setCustomImageOptions(prev_1 => ({
        ...prev_1,
        isBorderRadius: isBorderRadius_0
      }));
    };
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== customImageOptions || $[67] !== editor) {
    t24 = () => {
      editor.chain().focus().setCustomImage(customImageOptions).run();
      setCustomImageOptions(DEFAULT_CUSTOM_IMAGE_OPTIONS);
    };
    $[66] = customImageOptions;
    $[67] = editor;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = maxWidth_0 => {
      setCustomImageOptions(prev_2 => ({
        ...prev_2,
        maxWidth: maxWidth_0
      }));
    };
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== customImageOptions || $[71] !== initialImageOptions || $[72] !== t21 || $[73] !== t24) {
    t26 = <ImageEditButton disabled={t21} selectedOptions={customImageOptions} onAlignChange={t22} onClick={initialImageOptions} onIsBorderRadiusChange={t23} onSubmit={t24} onWidthChange={t25} />;
    $[70] = customImageOptions;
    $[71] = initialImageOptions;
    $[72] = t21;
    $[73] = t24;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== editor || $[76] !== toast || $[77] !== toastError || $[78] !== youTubeUrl) {
    t27 = () => {
      if (youTubeUrl) {
        ;
        try {
          const videoId = getYouTubeVideoId(youTubeUrl);
          editor.chain().focus().setCustomYouTubeIframe({
            src: `https://www.youtube.com/embed/${videoId}`
          }).run();
          setYouTubeUrl("");
        } catch (t28) {
          const error = t28;
          if (error instanceof Error && error.message) {
            toast({
              type: "fail",
              message: error.message
            });
            return;
          }
          toastError({
            error
          });
        }
      }
    };
    $[75] = editor;
    $[76] = toast;
    $[77] = toastError;
    $[78] = youTubeUrl;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== t27 || $[81] !== youTubeUrl) {
    t28 = <YouTubeButton youTubeUrl={youTubeUrl} onSubmit={t27} onYouTubeUrlChange={setYouTubeUrl} />;
    $[80] = t27;
    $[81] = youTubeUrl;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  let t30;
  let t31;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = href => {
      setCustomLinkOptions(prev_3 => ({
        ...prev_3,
        href
      }));
    };
    t30 = isOutLink => {
      setCustomLinkOptions(prev_4 => ({
        ...prev_4,
        isOutLink
      }));
    };
    t31 = size => {
      setCustomLinkOptions(prev_5 => ({
        ...prev_5,
        size
      }));
    };
    $[83] = t29;
    $[84] = t30;
    $[85] = t31;
  } else {
    t29 = $[83];
    t30 = $[84];
    t31 = $[85];
  }
  let t32;
  if ($[86] !== customLinkOptions || $[87] !== editor) {
    t32 = () => {
      editor.chain().focus().setCustomLink(customLinkOptions).run();
      setCustomLinkOptions(DEFAULT_CUSTOM_LINK_OPTIONS);
    };
    $[86] = customLinkOptions;
    $[87] = editor;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = theme => {
      setCustomLinkOptions(prev_6 => ({
        ...prev_6,
        theme
      }));
    };
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== customLinkOptions || $[91] !== initialLinkOptions || $[92] !== t32) {
    t34 = <LinkButton selectedOptions={customLinkOptions} onClick={initialLinkOptions} onHrefChange={t29} onIsOutLinkChange={t30} onSizeChange={t31} onSubmit={t32} onThemeChange={t33} />;
    $[90] = customLinkOptions;
    $[91] = initialLinkOptions;
    $[92] = t32;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <PreviewButton />;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  let t36;
  if ($[95] !== t10 || $[96] !== t11 || $[97] !== t12 || $[98] !== t13 || $[99] !== t16 || $[100] !== t19 || $[101] !== t20 || $[102] !== t26 || $[103] !== t28 || $[104] !== t34 || $[105] !== t8 || $[106] !== t9) {
    t36 = <nav className={t8}>{t9}{t10}{t11}{t12}{t13}{t16}{t19}{t20}{t26}{t28}{t34}{t35}</nav>;
    $[95] = t10;
    $[96] = t11;
    $[97] = t12;
    $[98] = t13;
    $[99] = t16;
    $[100] = t19;
    $[101] = t20;
    $[102] = t26;
    $[103] = t28;
    $[104] = t34;
    $[105] = t8;
    $[106] = t9;
    $[107] = t36;
  } else {
    t36 = $[107];
  }
  return t36;
};
function _temp(prev) {
  return {
    ...prev,
    theme: "image"
  };
}
function _temp2(fontSize_0) {
  return <option key={fontSize_0} className={fontSize_0} value={fontSize_0}>{fontSize_0}</option>;
}
function _temp3(textAlign_0) {
  return <option key={textAlign_0} value={textAlign_0}>{textAlign_0}</option>;
}